/**
 * Parse [[ASK]] blocks from AI messages
 *
 * The AI agent embeds structured JSON blocks within its response using
 * [[ASK]]...[[/ASK]] delimiters when it needs clarifying information
 * from the user.
 */

import type { AskBlock, AskAnswers, AskQuestion, AskWidgetType, ParsedMessage } from "@/types/aiBlocks";
import { CHAIN_WIDGET_TYPES, TOKEN_WIDGET_TYPES } from "@/types/aiBlocks";
import type { ResolvedSlackSelection } from "@/app/(v2)/workflows/_components/form-fields/SlackChannelSelector";
import { CHAIN_INFO } from "@/lib/chains";
import { parseLLMJsonBlock } from "@/utils/parseLLMBlock";

/** Resolve a chainId to a human-readable name (e.g. 8453 → "Base"). Returns
 *  null when the chain is not in our registry — callers should drop the line
 *  rather than echo a raw number like "8453" to the user. */
function resolveChainName(raw: unknown): string | null {
  const n = typeof raw === "number" ? raw : typeof raw === "string" ? Number(raw.trim()) : NaN;
  if (!Number.isFinite(n) || n <= 0) return null;
  return CHAIN_INFO[n]?.name ?? null;
}

/** Serialize a chain-id / chain-ids answer as comma-separated chain names.
 *  Returns null when none of the entries resolve, so the caller can skip the
 *  line entirely. */
function formatChainAnswer(answer: unknown): string | null {
  const raws = Array.isArray(answer) ? answer : [answer];
  const names = raws.map(resolveChainName).filter((n): n is string => !!n);
  return names.length > 0 ? names.join(", ") : null;
}

/** Identifies any question that ultimately renders as a SlackChannelSelector —
 *  covers the dedicated `slack-channel` widget, the JSON-schema variant
 *  (`properties.format === "slack-channel"`), and the array form
 *  (`properties.type === "array"` with `items.format === "slack-channel"`).
 *  Mirrors the routing in FormFieldConnector so we never lose workspace
 *  context for alternate AI-generated question shapes.
 *
 *  Exported so AskBlockRenderer (and any future consumer) uses the same rule
 *  — if the AI backend adds a new variant we only update this helper. */
export function isSlackChannelQuestion(q: AskQuestion): boolean {
  if (q.type === "slack-channel") return true;
  if (q.properties?.format === "slack-channel") return true;
  if (q.properties?.type === "array" && q.properties?.items?.format === "slack-channel") return true;
  return false;
}

const ASK_BLOCK_REGEX = /\[\[ASK\]\]\s*([\s\S]*?)\s*\[\[\/ASK\]\]/;

/**
 * Parse a message to extract the [[ASK]] block if present
 */
export function parseAskBlock(message: string): ParsedMessage {
  const { before, parsed, after } = parseLLMJsonBlock<AskBlock>(message, ASK_BLOCK_REGEX, "ASK");
  return { beforeAsk: before, askBlock: parsed, afterAsk: after };
}

/**
 * Check if a message contains an [[ASK]] block
 */
export function hasAskBlock(message: string): boolean {
  return ASK_BLOCK_REGEX.test(message);
}

/**
 * Remove [[ASK]] block from message (for display without form)
 */
export function stripAskBlock(message: string): string {
  return message.replace(ASK_BLOCK_REGEX, "").trim();
}

/**
 * Strip the "(channelId: …, workspaceId: …)" metadata suffix that
 * `formatSlackChannelAnswer` appends for the AI. Display paths (echoed
 * user-reply bubbles, the completed-ask pill) call this so users see
 * "#general in Acme Workspace" rather than the raw serialization.
 * Also handles comma-joined multi-select values.
 */
export function stripSlackChannelMetadata(raw: string): string {
  // Global regex instead of split(", ") so workspace names containing commas
  // (e.g. "Acme, Inc.") aren't cut at the wrong boundary before the strip runs.
  return raw.replace(/\s*\(channelId:[^)]*\)/g, "");
}

/**
 * Format a slack-channel answer. Accepts either a bare channel id string,
 * a single resolved object, or an array of resolved objects (multi-select).
 * Includes workspace name/id so the AI has enough context to proceed without
 * re-asking for the workspace.
 */
function formatSlackChannelAnswer(answer: unknown): string {
  const formatOne = (entry: unknown): string => {
    if (entry && typeof entry === "object") {
      const d = entry as Partial<ResolvedSlackSelection>;
      const channel = d.channelName ? `#${d.channelName}` : d.channelId || "";
      const workspace = d.workspaceName || d.workspaceId;
      const ids =
        d.channelId || d.workspaceId
          ? ` (channelId: ${d.channelId ?? "unknown"}${d.workspaceId ? `, workspaceId: ${d.workspaceId}` : ""})`
          : "";
      return workspace ? `${channel} in ${workspace}${ids}` : `${channel}${ids}`;
    }
    return String(entry);
  };
  if (Array.isArray(answer)) return answer.map(formatOne).join(", ");
  return formatOne(answer);
}

/**
 * Format user's answers into a message for the AI.
 * Each answer is prefixed with the question label for clarity.
 */
export function formatAskResponse(answers: AskAnswers, askBlock: AskBlock): string {
  const parts: string[] = [];
  const emittedIds = new Set<string>();

  for (const question of askBlock.questions) {
    const answer = answers[question.id];

    // Skip empty values (but allow false and 0)
    if (answer === undefined || answer === null || answer === "") continue;
    if (Array.isArray(answer) && answer.length === 0) continue;

    let formatted: string;

    // Chain-id / chain-ids: show resolved chain names (e.g. "Base"), not raw
    // chainId integers. If none resolve, skip the line — an unresolved number
    // is both unfriendly to the user (echoed in the reply bubble) and useless
    // context for Caddie.
    if (CHAIN_WIDGET_TYPES.has(question.type as AskWidgetType)) {
      const chainFormatted = formatChainAnswer(answer);
      if (!chainFormatted) continue;
      formatted = chainFormatted;
    } else if (typeof answer === "boolean") {
      formatted = answer ? "Yes" : "No";
    } else if (Array.isArray(answer)) {
      // For select/multiselect with options, resolve labels
      if (question.options) {
        const labels = answer.map(v => {
          const opt = question.options!.find(o => o.value === String(v));
          return opt ? opt.label : String(v);
        });
        formatted = labels.join(", ");
      } else {
        formatted = answer.map(v => String(v)).join(", ");
      }
    } else if ((question.type === "select" || question.type === "multiselect") && question.options) {
      const opt = question.options.find(o => o.value === String(answer));
      formatted = opt ? opt.label : String(answer);
    } else if (question.type === "polymarket-outcome" && typeof answer === "object" && answer !== null) {
      const pm = answer as { market?: string; outcome?: string };
      formatted = pm.outcome ? `${pm.outcome} on ${pm.market || "Polymarket"}` : pm.market || JSON.stringify(answer);
    } else if (isSlackChannelQuestion(question)) {
      formatted = formatSlackChannelAnswer(answer);
    } else if (typeof answer === "object" && answer !== null) {
      formatted = JSON.stringify(answer);
    } else {
      formatted = String(answer);
    }

    emittedIds.add(question.id);
    parts.push(`${question.label}: ${formatted}`);
  }

  // Belt-and-braces: if any token-address question is in the block but its
  // paired chainId/chainIds answer wasn't serialized above (e.g. an older
  // corrector build stripped the sibling question entirely), synthesize a
  // "Chain: <Name>" line from `answers.chainId` / `answers.chainIds` so the
  // user's chain pick still reaches Caddie. Skip silently when the chain
  // can't be resolved to a known name.
  const hasTokenQuestion = askBlock.questions.some(q => TOKEN_WIDGET_TYPES.has(q.type as AskWidgetType));
  if (hasTokenQuestion) {
    const chainAlreadyEmitted = askBlock.questions.some(
      q => CHAIN_WIDGET_TYPES.has(q.type as AskWidgetType) && emittedIds.has(q.id),
    );
    if (!chainAlreadyEmitted) {
      const chainFormatted = formatChainAnswer(answers.chainId ?? answers.chainIds);
      if (chainFormatted) parts.push(`Chain: ${chainFormatted}`);
    }
  }

  return parts.join("\n");
}
